/** @jsx jsx */
import React from 'react';
import { useSelector } from 'react-redux';
import { css, jsx } from '@emotion/core';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Form, Formik, Field } from 'formik';
import { CheckboxWithLabel, RadioGroup } from 'formik-material-ui';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import {
  CGULabel,
  InputFieldWithLabel,
  QuestionTooltip,
  PrimaryButton,
} from 'components';
import {
  appConfigSelector,
  loginLoadingSelector,
  clientIDLabelSelector,
} from 'reducers/app.reducer';
import { ICustomTheme, SignUpTypes } from 'interfaces';
import { ROUTES } from '../../constants';
import {
  StyledErrorMessage,
  CheckboxesContainer,
  InputsContainer,
  InputsInline,
  ButtonContainer,
  muiStyles,
} from './SignUpForm.styles';
import { TitleWithUIGuidelines } from 'component-library/atoms/TitleWithUIGuidelines';
import { LabelWithUIGuidelines } from 'component-library/atoms/LabelWithUIGuidelines';
import { BasicTextWithUIGuidelines } from 'component-library/atoms/BasicTextWithUIGuidelines';
import { LinkWithUIGuidelines } from 'component-library/atoms/LinkWithUIGuidelines';

type SignUpFormTypes = {
  handleSignUp: (value: SignUpTypes) => void;
  initialValues: SignUpTypes;
  userValidationSchema: Yup.ObjectSchema;
  isCompanyNameRequired: boolean;
  isReferralFeatureEnable?: boolean;
};

const useStyles = makeStyles(muiStyles);

const SignUpForm = ({
  handleSignUp,
  initialValues,
  userValidationSchema,
  isCompanyNameRequired,
  isReferralFeatureEnable = false,
}: SignUpFormTypes): JSX.Element => {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const classes = useStyles({ isMobile });
  const { programUrl, programName, companyName, dataProtectionsPath } =
    useSelector(appConfigSelector);
  const loginLoading = useSelector(loginLoadingSelector);
  const clientIDLabel =
    useSelector(clientIDLabelSelector) || t('auth.signup.matricule');

  const cssForm = css({
    marginTop: theme.spacing(3),
  });

  return (
    <React.Fragment>
      <TitleWithUIGuidelines title={t('auth.signup.title')} />
      <Formik
        initialValues={initialValues}
        validationSchema={userValidationSchema}
        onSubmit={handleSignUp}
      >
        <Form css={cssForm}>
          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              css={{
                marginBottom: theme.spacing(2),
                fontSize: isMobile
                  ? theme.fontSizes.mobileLabel
                  : theme.fontSizes.desktopLabel,
                color: theme.colors.black.main,
                '> .MuiFormLabel-root.Mui-focused': {
                  color: `${theme.colors.paletteFromBO.main} !important`,
                },
              }}
            >
              {t('auth.signup.civility.label')}
            </FormLabel>
            <Field
              component={RadioGroup}
              aria-label="gender"
              name="gender"
              id="gender"
            >
              <Grid container spacing={3}>
                <Grid item>
                  <FormControlLabel
                    value="0"
                    control={<Radio />}
                    label={t('auth.signup.civility.mrs')}
                    classes={{ label: classes.label }}
                  />
                </Grid>
                <Grid item>
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label={t('auth.signup.civility.mr')}
                    classes={{ label: classes.label }}
                  />
                </Grid>
              </Grid>
            </Field>
          </FormControl>
          <StyledErrorMessage
            name="gender"
            component={(props) => (
              <Typography
                {...props}
                variant="caption"
                css={{ display: 'block' }}
              />
            )}
          />
          <InputsContainer>
            <InputsInline>
              <InputFieldWithLabel
                label={t('auth.signup.lastname')}
                name="lastName"
                placeholder={t('auth.signup.lastname_placeholder')}
              />
              <InputFieldWithLabel
                label={t('auth.signup.firstname')}
                name="firstName"
                placeholder={t('auth.signup.firstname_placeholder')}
              />
            </InputsInline>
            {isCompanyNameRequired && (
              <InputFieldWithLabel
                label={t('auth.signup.companyName')}
                name="companyName"
                placeholder={t('auth.signup.companyPlaceholder')}
              />
            )}

            <InputsInline>
              <InputFieldWithLabel
                label={t('auth.global.email')}
                name="username"
                placeholder={t('auth.global.email_placeholder')}
                addons={
                  <div className={classes.tooltip}>
                    <QuestionTooltip
                      tooltipText={t('auth.signup.tooltipTextEmail')}
                      iconColor={theme.colors.paletteFromBO.main}
                    />
                  </div>
                }
                type="email"
              />
              <InputFieldWithLabel
                label={clientIDLabel}
                name="clientId"
                placeholder={
                  clientIDLabel || t('auth.signup.matricule_placeholder')
                }
                addons={
                  <div className={classes.tooltip}>
                    <QuestionTooltip
                      tooltipText={t('auth.signup.tooltipTextMatricule')}
                      iconColor={theme.colors.paletteFromBO.main}
                    />
                  </div>
                }
                textCase="sentence"
              />
            </InputsInline>

            <InputsInline>
              <InputFieldWithLabel
                label={t('auth.global.password')}
                name="password"
                placeholder={t('auth.global.password_placeholder')}
                textCase="sentence"
                type="password"
              />
              <InputFieldWithLabel
                label={t('auth.global.confirm_password')}
                name="confirmPassword"
                placeholder={t('auth.global.confirm_password_placeholder')}
                textCase="sentence"
                type="password"
              />
            </InputsInline>
            {isReferralFeatureEnable && (
              <InputFieldWithLabel
                label={t('auth.signup.referral.label')}
                name="referralCode"
                placeholder={t('auth.signup.referral.placeholder')}
                addons={(
                  <div className={classes.tooltip}>
                    <QuestionTooltip
                      tooltipText={t('auth.signup.referral.tooltip')}
                      iconColor={theme.colors.paletteFromBO.main}
                    />
                  </div>
                )}
              />
            )}
          </InputsContainer>
          <CheckboxesContainer>
            <FormControlLabel
              control={
                <Field
                  component={CheckboxWithLabel}
                  type="checkbox"
                  name="optin"
                  id="optin"
                />
              }
              label={<LabelWithUIGuidelines label={t('auth.signup.contact')} />}
            />

            <FormControlLabel
              control={
                <Field
                  component={CheckboxWithLabel}
                  type="checkbox"
                  name="terms"
                  id="terms"
                />
              }
              label={<CGULabel />}
            />
            <div
              css={{
                marginLeft: theme.spacing(-1.5),
                marginBottom: theme.spacing(1.5),
              }}
            >
              <StyledErrorMessage
                name="terms"
                component={(props) => (
                  <Typography
                    {...props}
                    variant="caption"
                    css={{ display: 'block' }}
                  />
                )}
              />
            </div>
          </CheckboxesContainer>
          <BasicTextWithUIGuidelines
            text={t('auth.signup.dataPolicy', { programName, companyName })}
          />
          <LinkWithUIGuidelines
            color={theme.colors.paletteFromBO.main}
            fontWeight="bold"
            url={`${programUrl}${dataProtectionsPath}`}
            textLink={t('auth.signup.dataPolicyLink')}
            isInternalLink={false}
            withUnderline
          />
          <ButtonContainer>
            <PrimaryButton
              label={t('auth.signup.submit')}
              isDisabled={loginLoading}
              isLoading={loginLoading}
            />
          </ButtonContainer>
          <div css={{ textAlign: 'center' }}>
            <BasicTextWithUIGuidelines
              text={t('auth.global.iAlreadyHaveAnAccount')}
            />{' '}
            <LinkWithUIGuidelines
              color={theme.colors.paletteFromBO.main}
              fontWeight="bold"
              url={ROUTES.LOGIN}
              textLink={t('auth.global.login')}
              isInternalLink={true}
              withUnderline
            />
          </div>
        </Form>
      </Formik>
    </React.Fragment>
  );
};

export default SignUpForm;
